import { useEffect, useMemo, useState, type ComponentType } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { getDocLoader } from '../lib/content';
import { useTranslation, type Locale } from '../i18n';

interface Props {
  lang: Locale;
}

/**
 * IMPORTANT: every hook must run in the same order on every render.
 * The previous implementation did `return ... 404` before a useMemo, which
 * made the hook count vary across renders and produced
 * "Rendered fewer hooks than expected" in the console. The pattern now is:
 *   1. Call every hook unconditionally
 *   2. Branch on state values for the return shape
 */
export default function DocPage({ lang }: Props) {
  const t = useTranslation();
  const location = useLocation();

  const slug = useMemo(() => {
    const parts = location.pathname.split('/').filter(Boolean);
    return parts.slice(1).join('/') || 'overview';
  }, [location.pathname]);

  // Tri-state: undefined = loading, null = 404, Component = ready.
  const [Doc, setDoc] = useState<ComponentType | null | undefined>(undefined);

  useEffect(() => {
    const loader = getDocLoader(lang, slug);
    if (!loader) {
      setDoc(null);
      return;
    }
    setDoc(undefined);
    let cancelled = false;
    loader().then(
      (mod) => { if (!cancelled) setDoc(() => mod.default); },
      ()    => { if (!cancelled) setDoc(null); },
    );
    return () => { cancelled = true; };
  }, [lang, slug]);

  if (Doc === null) {
    return (
      <div>
        <h1>{t('404.title')}</h1>
        <p>{t('404.body')}</p>
        <p><Link to={`/${lang}/overview`}>{t('404.back')}</Link></p>
      </div>
    );
  }
  if (Doc === undefined) {
    return <div style={{ color: 'var(--c-muted)' }}>Loading…</div>;
  }
  return <Doc />;
}
